/* ==========================================================================
   TOWN SYSTEM — shared behavior. Loaded on every page, before section JS.
   Sections must use these helpers instead of writing their own scroll locks,
   escape handlers or accordions. Three competing scroll locks is how you get
   a page that only unfreezes on Escape.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  /* --- Scroll lock ------------------------------------------------------
     Reason-counted: the mega menu, the filter drawer and a modal can all be
     open in sequence without one release clearing the others. Always pass a
     stable reason string.
  --------------------------------------------------------------------- */
  var lockReasons = new Set();

  function lockScroll(reason) {
    lockReasons.add(reason || 'anon');
    root.classList.add('town-scroll-locked');
  }

  function unlockScroll(reason) {
    lockReasons.delete(reason || 'anon');
    if (lockReasons.size === 0) root.classList.remove('town-scroll-locked');
  }

  function releaseAllScroll() {
    lockReasons.clear();
    root.classList.remove('town-scroll-locked');
  }

  // A bfcache restore or a back-navigation must never leave the page frozen.
  window.addEventListener('pagehide', releaseAllScroll);
  window.addEventListener('popstate', releaseAllScroll);

  /* --- Overlay stack ---------------------------------------------------
     Escape closes only the topmost overlay. Register with an onClose that
     is idempotent.
  --------------------------------------------------------------------- */
  var overlayStack = [];

  function pushOverlay(handle) {
    overlayStack.push(handle);
  }

  function popOverlay(handle) {
    var i = overlayStack.lastIndexOf(handle);
    if (i > -1) overlayStack.splice(i, 1);
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || overlayStack.length === 0) return;
    var top = overlayStack[overlayStack.length - 1];
    if (top && typeof top.close === 'function') {
      e.stopPropagation();
      top.close();
    }
  });

  /* --- Accordion -------------------------------------------------------
     Markup contract:
       <div class="town-acc" data-town-acc [data-town-acc-single]>
         <div class="town-acc__item">
           <button class="town-acc__trigger" aria-expanded="false" aria-controls="ID">
             Label <span class="town-acc__icon"></span>
           </button>
           <div class="town-acc__panel" id="ID" hidden><div class="town-acc__inner">…</div></div>
         </div>
       </div>
     Animates real heights so content of any length works.
  --------------------------------------------------------------------- */
  function setPanel(panel, open) {
    var inner = panel.firstElementChild;
    if (!inner) return;
    if (open) {
      panel.hidden = false;
      panel.style.height = inner.offsetHeight + 'px';
      panel.addEventListener(
        'transitionend',
        function onEnd(e) {
          if (e.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.style.height = 'auto';
        },
        { once: false }
      );
    } else {
      panel.style.height = panel.offsetHeight + 'px';
      // Force a reflow so the height transition has a start value.
      void panel.offsetHeight;
      panel.style.height = '0px';
      panel.addEventListener(
        'transitionend',
        function onEnd(e) {
          if (e.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.hidden = true;
        },
        { once: false }
      );
    }
  }

  function initAccordions(scope) {
    (scope || document).querySelectorAll('[data-town-acc]').forEach(function (acc) {
      if (acc.dataset.townAccReady) return;
      acc.dataset.townAccReady = '1';
      var single = acc.hasAttribute('data-town-acc-single');

      acc.addEventListener('click', function (e) {
        var trigger = e.target.closest('.town-acc__trigger');
        if (!trigger || !acc.contains(trigger)) return;
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        var willOpen = trigger.getAttribute('aria-expanded') !== 'true';

        if (single && willOpen) {
          acc.querySelectorAll('.town-acc__trigger[aria-expanded="true"]').forEach(function (other) {
            if (other === trigger) return;
            other.setAttribute('aria-expanded', 'false');
            var op = document.getElementById(other.getAttribute('aria-controls'));
            if (op) setPanel(op, false);
          });
        }

        trigger.setAttribute('aria-expanded', String(willOpen));
        setPanel(panel, willOpen);
      });
    });
  }

  /* --- Tabs ------------------------------------------------------------
     Markup contract:
       <div data-town-tabs>
         <div class="town-tabs" role="tablist">
           <button class="town-tabs__btn" role="tab" aria-selected="true" aria-controls="ID">…</button>
         </div>
         <div id="ID" role="tabpanel">…</div>
       </div>
  --------------------------------------------------------------------- */
  function initTabs(scope) {
    (scope || document).querySelectorAll('[data-town-tabs]').forEach(function (wrap) {
      if (wrap.dataset.townTabsReady) return;
      wrap.dataset.townTabsReady = '1';
      var btns = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));

      function select(btn) {
        btns.forEach(function (b) {
          var on = b === btn;
          b.setAttribute('aria-selected', String(on));
          b.tabIndex = on ? 0 : -1;
          var p = document.getElementById(b.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
      }

      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[role="tab"]');
        if (btn && btns.indexOf(btn) > -1) select(btn);
      });

      wrap.addEventListener('keydown', function (e) {
        var i = btns.indexOf(document.activeElement);
        if (i < 0) return;
        var next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : -1;
        if (next < 0 || next >= btns.length) return;
        e.preventDefault();
        btns[next].focus();
        select(btns[next]);
      });

      select(wrap.querySelector('[role="tab"][aria-selected="true"]') || btns[0]);
    });
  }

  /* --- Sticky buy bar --------------------------------------------------
     Shows the bar once the real add-to-cart button has scrolled out of view.
     Pass the element to observe via data-town-buybar-watch="#selector".
  --------------------------------------------------------------------- */
  function initBuyBars(scope) {
    (scope || document).querySelectorAll('[data-town-buybar]').forEach(function (bar) {
      if (bar.dataset.townBuybarReady) return;
      bar.dataset.townBuybarReady = '1';
      var watchSel = bar.getAttribute('data-town-buybar-watch');
      var watch = watchSel ? document.querySelector(watchSel) : null;
      if (!watch) {
        bar.classList.add('is-visible');
        return;
      }
      new IntersectionObserver(
        function (entries) {
          bar.classList.toggle('is-visible', !entries[0].isIntersecting);
        },
        { rootMargin: '0px 0px -40px 0px' }
      ).observe(watch);
    });
  }

  /* --- Countdown to the daily print cutoff -----------------------------
     <span data-town-cutoff="17:00" data-town-cutoff-tz="America/Chicago"></span>
     Renders "04h 21m". Shop time, not the visitor's timezone — the cutoff is
     a fact about our press, not about where the customer is sitting.
  --------------------------------------------------------------------- */
  function shopNow(tz) {
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
    } catch (e) {
      return new Date();
    }
  }

  function initCutdowns(scope) {
    var els = (scope || document).querySelectorAll('[data-town-cutoff]');
    if (!els.length) return;

    function tick() {
      els.forEach(function (el) {
        var parts = (el.getAttribute('data-town-cutoff') || '17:00').split(':');
        var tz = el.getAttribute('data-town-cutoff-tz') || 'America/Chicago';
        var now = shopNow(tz);
        var target = new Date(now);
        target.setHours(parseInt(parts[0], 10), parseInt(parts[1] || '0', 10), 0, 0);
        var diff = target - now;
        if (diff <= 0) {
          el.textContent = el.getAttribute('data-town-cutoff-passed') || 'tomorrow';
          el.closest('[data-town-cutoff-wrap]')?.classList.add('is-passed');
          return;
        }
        var h = Math.floor(diff / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        el.textContent = String(h).padStart(2, '0') + 'h ' + String(m).padStart(2, '0') + 'm';
      });
    }

    tick();
    setInterval(tick, 30000);
  }

  /* --- Public surface -------------------------------------------------- */
  window.Town = {
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    releaseAllScroll: releaseAllScroll,
    pushOverlay: pushOverlay,
    popOverlay: popOverlay,
    initAll: function (scope) {
      initAccordions(scope);
      initTabs(scope);
      initBuyBars(scope);
      initCutdowns(scope);
    }
  };

  function boot() {
    window.Town.initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    window.Town.initAll(e.target);
  });
})();
